import { motion, AnimatePresence } from "framer-motion";
import { X, Users, Globe2, Star, ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";

export default function ServiceModal({ service, onClose }) {
  const { content, openBooking } = useSite();
  const countries = content.client_countries || [];
  const flags = (service?.supported_countries || [])
    .map((name) => countries.find((c) => c.name === name) || { name, flag_url: "" })
    .filter((c) => c.name);

  return (
    <AnimatePresence>
      {service && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-[rgba(4,9,26,0.78)] backdrop-blur-sm"
          onClick={onClose}
          data-testid="service-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 34, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-3xl border border-[rgba(0,207,255,0.55)] overflow-hidden max-h-[88vh] overflow-y-auto"
            style={{
              background: "rgba(9, 18, 36, 0.72)",
              backdropFilter: "blur(22px)",
              boxShadow: "0 0 0 1px rgba(0,207,255,0.25), 0 0 48px rgba(0,191,255,0.30), 0 34px 90px rgba(0,0,0,0.65)",
            }}
            data-testid="service-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
              aria-label="Close"
              data-testid="service-modal-close"
            >
              <X size={17} />
            </button>

            <div className="p-7 sm:p-9">
              <div className="flex items-center gap-4">
                <span className="w-14 h-14 rounded-2xl bg-[rgba(0,207,255,0.14)] border border-[rgba(0,207,255,0.4)] flex items-center justify-center flex-shrink-0">
                  <service.icon size={26} className="text-[#00CFFF]" strokeWidth={2.1} />
                </span>
                <h3 className="font-display font-extrabold text-white text-2xl leading-snug pr-10" data-testid="service-modal-title">
                  {service.title}
                </h3>
              </div>

              <p className="mt-5 text-[15px] text-slate-300 leading-relaxed" data-testid="service-modal-description">
                {service.description}
              </p>

              <div className="mt-5 flex items-center gap-2.5 rounded-xl bg-[rgba(0,207,255,0.08)] border border-[rgba(0,207,255,0.25)] px-4 py-3">
                <Users size={16} className="text-[#00CFFF] flex-shrink-0" />
                <span className="text-sm font-bold text-white" data-testid="service-modal-specialists">
                  Available now: {service.available_specialists ?? 12} specialists
                </span>
              </div>

              <div className="mt-6 pt-5 border-t border-white/12">
                <div className="flex items-center gap-2 mb-3">
                  <Globe2 size={15} className="text-[#00CFFF]" />
                  <span className="eyebrow text-[10px] text-[#8FA3C8]">Countries we support</span>
                </div>
                <ul className="space-y-2" data-testid="service-modal-countries">
                  {flags.map((c) => (
                    <li key={c.name} className="flex items-center gap-2.5 text-sm text-slate-200 font-medium">
                      {c.flag_url ? (
                        <img src={c.flag_url} alt="" className="w-5 h-5 rounded-full object-cover border border-white/20 flex-shrink-0" />
                      ) : (
                        <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">{c.name[0]}</span>
                      )}
                      {c.name}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 flex items-center gap-2 text-sm text-[#8FD8FF]">
                <Star size={15} className="text-[#F5C542] fill-[#F5C542] flex-shrink-0" />
                <span data-testid="service-modal-stat">Trusted by 160+ brands worldwide</span>
              </div>

              <button
                className="btn btn-primary w-full mt-7 justify-center"
                onClick={() => {
                  onClose();
                  openBooking(null, "booking");
                }}
                data-testid="service-modal-book"
              >
                Book a Free Consultation <ArrowRight size={16} strokeWidth={2.5} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
